
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { DeviceInfo, DeviceType, SharedDeviceInfo, getDefaultShareInfo } from '@/types/device-info';
import DeviceOverviewInfoRow from './DeviceOverviewInfoRow.vue';
import DeviceOptionsMenu from '@/components/devices/menus/DeviceOptionsMenu.vue';
import DeviceTrackingMenu from '@/components/devices/menus/DeviceTrackingMenu.vue';
import DeviceSharesRendrer from '@/components/devices/DeviceSharesRendrer.vue';
import ShareEditPopup from '@/components/devices/popups/ShareEditPopup.vue';
import api from '@/modules/api';
import { PRIVILEGES_OWNER } from '@/utils/constants';
import { ESimStatus } from '@/constants/esimStatus';
import { Action, State } from 'vuex-class';
import { LoadDevicesActionType, LoadDeviceActionType } from '@/types/store/actions';
import { UserInfo } from '../../types/user-info';
import { actions, mutations } from '@/store/types';
import { voltage_to_capacity } from '@/utils/voltageToCapacity';
import { Subscription } from '@/types/subscriptions';
import LocateContinuesModal from '@/components/devices/popups/LocateContinuesModal.vue';
import { getErrorMessage } from '@/utils/common';
import { Mutation } from 'vuex-class';
import { SetDeviceMutationType, SetDevicesMutationType } from '@/types/store/mutations';
import { GPSReportModes } from '../../types/device-info';
import { get_edrx_status, get_psm_status, get_operating_mode } from '@/utils/devices';
import { validatePrivilege } from '@/utils/devices';
import { DevicePrivileges } from '@/constants/device_privileges';
import { operatingModeOptions, defaultOperatingMode } from '@/constants/options';
import { handleTempUnit } from '@/utils/temperature';
import * as dlg from '@/modules/dlg';
import AirplaneModeUpdatePopup from '@/components/devices/popups/AirplaneModeUpdatePopup.vue';
import { defaultCellScanLimit } from '@/constants/constants';
import { userRoles } from '@/constants/userRoles';

@Component({
    components: {
        DeviceOverviewInfoRow,
        DeviceOptionsMenu,
        DeviceTrackingMenu,
        DeviceSharesRendrer,
        ShareEditPopup,
        LocateContinuesModal,
        AirplaneModeUpdatePopup,
    },
})
export default class DeviceInfoComponent extends Vue {
    share: SharedDeviceInfo | null = null;
    ManageSharingTitle = 'Share';
    voltage_to_capacity = voltage_to_capacity;
    get_edrx_status = get_edrx_status;
    get_psm_status = get_psm_status;
    get_operating_mode = get_operating_mode;
    protected continues = false;
    protected locateContinueModalShow = false;
    protected validatePrivilege = validatePrivilege;
    protected handleTempUnit = handleTempUnit;
    DevicePrivileges = DevicePrivileges;
    operatingMode = defaultOperatingMode;
    operatingModeOptions = operatingModeOptions;
    protected defaultOperatingMode = defaultOperatingMode;
    pushNotification = false;
    emailAlerts = false;

    // protected modemInfo = null;
    modemLoading = false;

    @Action(actions.LOAD_DEVICES)
    loadDevices!: LoadDevicesActionType;

    @Action(actions.LOAD_DEVICE)
    loadDevice!: LoadDeviceActionType;

    @Mutation(mutations.SET_CURRENT_DEVICE)
    setCurrentDevice!: SetDeviceMutationType;

    @Mutation(mutations.SET_DEVICES)
    setDevices!: SetDevicesMutationType;

    @State('user')
    user!: UserInfo;

    @State('device')
    current!: DeviceInfo;

    @State('devices')
    devices!: DeviceInfo[];

    @State('subscriptions')
    userSubscriptions!: Subscription[];

    @Prop({ required: true })
    device!: DeviceInfo;

    DeviceTypeenum = DeviceType;
    airplaneModeDialog = false;

    handleAirPlanMode() {
        this.airplaneModeDialog = true;
    }

    validateAccess(privileges: number[] = []) {
        return this.validatePrivilege(this.device.privilege, privileges);
    }

    validateRestricted() {
        return this.validateAccess([DevicePrivileges.SuperOwner, DevicePrivileges.Owner]);
    }

    onCheckboxClick() {
        this.continues = !this.continues;
    }

    get alarmClass() {
        return `notify-btn ${this.device.alarm === true ? 'success-btn' : ''}`;
    }

    get deviceSubscriptionStatus() {
        return this.device?.subscription?.status ? this.device?.subscription?.status : 'N/A';
    }

    get deviceCellularPings() {
        return `${this.device?.cellRequestsCount || 0} / ${this.device.cellScanLimit}`;
    }

    get simStatus() {
        return this.device?.esim ? (this.device?.esim.status === ESimStatus.Active ? 'Active' : 'Inactive') : 'N/A';
    }

    get deviceId() {
        if (!this.device) return '';

        if (this.device.deviceType === DeviceType.AirboltGps) {
            if (!this.device.imei) return '';
            const parts = this.device.imei.match(/.{1,4}/g);
            return parts ? parts.join(':') : '';
        }

        return this.device.deviceUUID ?? '';
    }

    get deviceIdtitle() {
        return this.device.deviceType === DeviceType.AirboltGps ? 'eSIM ID' : 'Device UUID';
    }

    get deviceTemperature() {
        if (this.device.modem_temperature) {
            return `${this.device.modem_temperature} °${this.device.temperature?.unit?.toUpperCase() ?? 'C'}`;
            // return handleTempUnit(this.device.modem_temperature, this.device.temperature?.unit ?? 'c');
        }
        return 'N/A';
    }

    async add() {
        this.share = getDefaultShareInfo({
            deviceUUID: this.device.deviceUUID,
        });
    }

    async refresh() {
        await this.loadDevices({ userId: this.user._id as string });
        if (this.current && this.current._id) {
            await this.loadDevice({ deviceUUID: this.current.deviceUUID });
        }
    }

    async save(share: SharedDeviceInfo) {
        this.$store.commit(mutations.LOADING, { loading: true });
        if (share._id) {
            try {
                await api.updateShare({
                    ...share,
                    isOwnerFlag: share.accessRightsFlag === PRIVILEGES_OWNER ? 1 : 0,
                });
                await this.refresh();
                this.share = null;
                Vue.toasted.success('Updated successfully');
            } catch (error) {
                Vue.toasted.error(getErrorMessage(error));
            } finally {
                this.$store.commit(mutations.LOADING, { loading: false });
            }
        } else {
            try {
                await api.createShare({
                    ...share,
                    masterKey: this.device.passcode,
                    isOwnerFlag: share.accessRightsFlag === PRIVILEGES_OWNER ? 1 : 0,
                });
                await this.refresh();
                this.share = null;
                Vue.toasted.success('Shared successfully');
            } catch (error) {
                Vue.toasted.error(getErrorMessage(error));
            } finally {
                this.$store.commit(mutations.LOADING, { loading: false });
            }
        }
    }

    handleSettingsClick(device: DeviceInfo) {
        if (this.user?.roles.includes(userRoles.SharedUser)) {
            this.$router
                .push({ name: 'device-settings-alert', params: { deviceUUID: device.deviceUUID as string } })
                .catch((error) => error);
        } else {
            this.$router
                .push({ name: 'device-settings', params: { deviceUUID: device.deviceUUID as string } })
                .catch((error) => error);
        }
    }

    handleLocateDeviceClick() {
        // this.continues = false;
        this.locateContinueModalShow = true;
    }

    async alarm() {
        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            const response = await api.updateDeviceAlarm(
                this.device.deviceUUID,
                this.device.alarm === true ? 'off' : 'on',
            );

            const updatedDevices = this.devices.map((item) => {
                if (item.deviceUUID === this.device.deviceUUID) return { ...item, alarm: !this.device.alarm };
                return item;
            });
            this.setDevices({ devices: updatedDevices });
            this.setCurrentDevice({ device: { ...this.current, alarm: !this.device.alarm } });
            Vue.toasted.success(response.message);
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }

    async onLocate(mode: string) {
        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            await api.locateDevice(this.device.deviceUUID, mode);
            this.setCurrentDevice({ device: { ...this.current, locationReportMode: mode } });
            this.locateContinueModalShow = false;
            Vue.toasted.success('Requested successfully');
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }

    async stopTracking() {
        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            await api.stopLiveTracking(this.device.deviceUUID);
            this.setCurrentDevice({ device: { ...this.current, locationReportMode: GPSReportModes.Once } });
            Vue.toasted.success('Live tracking disabled');
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }

    async setOperatingMode(operatingMode: string) {
        const confirmed = await dlg.confirm('Are you sure you want to change operating mode?', 'Operating mode');
        if (!confirmed) {
            this.operatingMode =
                operatingMode === this.defaultOperatingMode
                    ? this.operatingModeOptions[1].value
                    : this.defaultOperatingMode;
            return;
        }

        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            await api.updateOperatingMode(this.device._id as string, operatingMode);
            this.setCurrentDevice({ device: { ...this.current, operatingMode } });
            Vue.toasted.success('Updated successfully');
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }

    async updateAlertSettings() {
        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            await api.updateDeviceAlerts(this.device._id as string, this.pushNotification, this.emailAlerts);
            this.setCurrentDevice({
                device: { ...this.current, pushNotification: this.pushNotification, emailAlerts: this.emailAlerts },
            });
            Vue.toasted.success('Updated successfully');
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }

    @Watch('device', { immediate: true, deep: true })
    onDeviceChanged(device: DeviceInfo) {
        if (device && device.deviceType === this.DeviceTypeenum.AirboltGps) {
            this.operatingMode = device.operatingMode ?? this.defaultOperatingMode;
            this.pushNotification = device.pushNotification ?? false;
            this.emailAlerts = device.emailAlerts ?? false;
            this.device.alarm = device.alarm;
        }
    }

    notSharedUser() {
        return !this.user?.roles.includes(userRoles.SharedUser);
    }
}
